import { useId, type SVGProps } from "react";

import { brandIconA11y } from "@/components/icons/brandIconA11y";

/**
 * Microsoft Azure brand mark — the current gradient "A".
 *
 * Two things this is deliberately NOT:
 * - Not `faMicrosoft`. The Microsoft four-square is the *corporate* logo and
 *   belongs on "Sign in with Microsoft", not on a cloud-provider row.
 * - Not the older flat `#0089D6` chevron. Microsoft refreshed the Azure mark to
 *   this gradient form; the flat one reads as dated next to current AWS/GCP art.
 *
 * Full colour on purpose. Provider marks are vendor identity, not UI accents —
 * the same reason AWS keeps its orange. `currentColor` is not usable here (the
 * mark is a three-gradient composite), so unlike the FontAwesome provider icons
 * this one ignores `text-azure`. Size it with `className="h-4 w-4"` and let the
 * colours be.
 *
 * Gradient ids are namespaced per instance via `useId`: fixed ids would collide
 * when several Azure icons render on one page (a credentials list does exactly
 * that) and every instance after the first would pick up the wrong fill.
 */
export function AzureIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  // `useId` emits colons, which are legal in HTML ids but hostile inside
  // `url(#…)` references — strip them.
  const uid = useId().replace(/:/g, "");
  const gA = `azure-a-${uid}`;
  const gShadow = `azure-shadow-${uid}`;
  const gB = `azure-b-${uid}`;

  return (
    <svg
      viewBox="0 0 256 242"
      xmlns="http://www.w3.org/2000/svg"
      {...brandIconA11y(props)}
      className={className}
      {...props}
    >
      <defs>
        <linearGradient
          id={gA}
          x1="58.972%"
          x2="37.191%"
          y1="7.411%"
          y2="103.762%"
        >
          <stop offset="0%" stopColor="#114a8b" />
          <stop offset="100%" stopColor="#0669bc" />
        </linearGradient>
        <linearGradient
          id={gShadow}
          x1="59.719%"
          x2="52.691%"
          y1="52.313%"
          y2="54.864%"
        >
          <stop offset="0%" stopOpacity=".3" />
          <stop offset="7.1%" stopOpacity=".2" />
          <stop offset="32.1%" stopOpacity=".1" />
          <stop offset="62.3%" stopOpacity=".05" />
          <stop offset="100%" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={gB}
          x1="37.279%"
          x2="62.473%"
          y1="4.6%"
          y2="99.979%"
        >
          <stop offset="0%" stopColor="#3ccbf4" />
          <stop offset="100%" stopColor="#2892df" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gA})`}
        d="M85.343.003h75.753L82.457 233a12.08 12.08 0 0 1-11.442 8.216H12.06A12.06 12.06 0 0 1 .633 225.303L73.898 8.219A12.08 12.08 0 0 1 85.343 0z"
      />
      <path
        fill="#0078d4"
        d="M195.423 156.282H75.297a5.56 5.56 0 0 0-3.796 9.627l77.19 72.047a12.14 12.14 0 0 0 8.28 3.26h68.02z"
      />
      <path
        fill={`url(#${gShadow})`}
        d="M85.343.003a11.98 11.98 0 0 0-11.471 8.376L.723 225.105a12.045 12.045 0 0 0 11.37 16.112h60.475a12.93 12.93 0 0 0 9.921-8.437l14.588-42.991l52.105 48.6a12.33 12.33 0 0 0 7.757 2.828h67.766l-29.721-84.935l-86.643.02L161.37.003z"
      />
      <path
        fill={`url(#${gB})`}
        d="M182.098 8.207A12.06 12.06 0 0 0 170.67.003H86.245c5.175 0 9.773 3.301 11.428 8.204L170.94 225.3a12.062 12.062 0 0 1-11.428 15.92h84.429a12.062 12.062 0 0 0 11.425-15.92z"
      />
    </svg>
  );
}
